import {
  AsyncButton,
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  useIsMobile,
} from '@bpinternal/ui-kit-next'
import { motion } from 'framer-motion'
import { AlertCircle, CheckCircle, ChevronRight, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import DarkSyncWebsiteSvg from '~/assets/sync-website-dark.svg?react'
import SyncWebsiteSvg from '~/assets/sync-website.svg?react'
import { AddKnowledgeSourceFooter } from '~/features/knowledge-bases/components/knowledge-source/AddKnowledgeSource/AddKnowledgeSourceFooter'
import { useDsData } from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { KnowledgeSourceStepProps } from '~/features/knowledge-bases/types'
import { useLocalStore } from '~/stores/localStore'
import { cn } from '~/utils'
import { useSyncWebsiteContext } from './SyncWebsiteContext'

type DiscoverPagesStepProps = {
  workspaceId: string
  botId: string
  kbId: string
  dsId?: string
  footerRef: React.RefObject<HTMLDivElement>
} & KnowledgeSourceStepProps

export const DiscoverPagesStep = ({ workspaceId, botId, kbId, dsId, close, footerRef }: DiscoverPagesStepProps) => {
  const { discoverUrls, discoverUrlsLoading } = useSyncWebsiteContext()
  const { dsData } = useDsData({ workspaceId, botId, kbId })
  const url = (dsId && dsData[dsId]?.title) || ''

  const [currentUrl, setCurrentUrl] = useState(url)
  const [includeGlobs, setIncludeGlobs] = useState<string[]>([])
  const [excludeGlobs, setExcludeGlobs] = useState<string[]>([])
  const [urlError, setUrlError] = useState('')
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false)

  const theme = useLocalStore((s) => s.getPreferredTheme())

  const handleDiscoverUrls = async () => {
    if (!currentUrl.trim() || urlError.length > 0) return
    discoverUrls(currentUrl, includeGlobs, excludeGlobs)
  }

  const validateUrl = (url: string) => {
    if (url.length === 0) {
      setUrlError('')
      return
    }

    try {
      const urlToValidate = url.includes('://') ? url : `https://${url}`
      new URL(urlToValidate)
      setUrlError('')
    } catch {
      setUrlError('Please enter a valid URL')
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value
    setCurrentUrl(url)
    validateUrl(url)
  }

  return (
    <>
      <div className="flex flex-row gap-6">
        <div className="flex flex-col gap-10">
          <WebsiteLink
            currentUrl={currentUrl}
            isLoading={discoverUrlsLoading}
            dsId={dsId}
            handleUrlChange={handleUrlChange}
            handleDiscoverUrls={handleDiscoverUrls}
            urlError={urlError}
          />
          <Collapsible open={advancedSettingsOpen} onOpenChange={setAdvancedSettingsOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-fit pr-2">
                Advanced settings{' '}
                <ChevronRight
                  className={cn(
                    'size-4 transition-transform duration-200 ease-out',
                    advancedSettingsOpen && 'rotate-90'
                  )}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <AdvancedSettings
                includeGlobs={includeGlobs}
                excludeGlobs={excludeGlobs}
                setAdvancedSettingsOpen={setAdvancedSettingsOpen}
                setIncludeGlobs={setIncludeGlobs}
                setExcludeGlobs={setExcludeGlobs}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
        <motion.div className="self-center" layout>
          {theme === 'dark' ? (
            <DarkSyncWebsiteSvg className="hidden h-64 w-auto flex-shrink-0 rounded-lg lg:block" />
          ) : (
            <SyncWebsiteSvg className="hidden h-64 w-auto flex-shrink-0 rounded-lg lg:block" />
          )}
        </motion.div>
      </div>
      <DiscoverPagesFooter
        footerRef={footerRef}
        close={close}
        handleDiscoverUrls={handleDiscoverUrls}
        isValidUrl={urlError.length === 0}
        currentUrl={currentUrl}
        isLoading={discoverUrlsLoading}
      />
    </>
  )
}

const WebsiteLink = ({
  currentUrl,
  isLoading,
  dsId,
  handleUrlChange,
  handleDiscoverUrls,
  urlError,
}: {
  currentUrl: string
  isLoading: boolean
  dsId?: string
  handleUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleDiscoverUrls: () => void
  urlError: string
}) => {
  return (
    <div className="flex w-full flex-col justify-between gap-4">
      <div className="flex flex-col gap-4">
        <p className="text-base font-medium text-foreground">Website link</p>
        <p className="mb-2 text-sm text-foreground">
          Please add a link to your Help Center or documentation where we can find answers to common product questions.
        </p>
        <div className="relative px-0.5">
          <Input
            id="url"
            type="url"
            value={currentUrl}
            disabled={isLoading || dsId !== undefined}
            onChange={handleUrlChange}
            onKeyUp={(e) =>
              e.key === 'Enter' && currentUrl && urlError.length === 0 && !isLoading && handleDiscoverUrls()
            }
            placeholder="https://app.com/Q&A"
            className={cn(
              'pr-10 text-sm',
              urlError && 'border-destructive focus-visible:ring-destructive',
              urlError.length === 0 && currentUrl && 'border-success focus-visible:ring-success'
            )}
          />
          {urlError.length === 0 && currentUrl && (
            <CheckCircle className="absolute right-3 top-2.5 h-4 w-4 text-success" />
          )}
          {urlError && <AlertCircle className="absolute right-3 top-2.5 h-4 w-4 text-destructive" />}
        </div>
        {urlError && <p className="text-xs text-destructive sm:text-sm">{urlError}</p>}
      </div>
    </div>
  )
}

const AdvancedSettings = ({
  includeGlobs,
  excludeGlobs,
  setIncludeGlobs,
  setExcludeGlobs,
}: {
  includeGlobs: string[]
  excludeGlobs: string[]
  setAdvancedSettingsOpen: (open: boolean) => void
  setIncludeGlobs: (globs: string[]) => void
  setExcludeGlobs: (globs: string[]) => void
}) => {
  const [includePattern, setIncludePattern] = useState('')
  const [excludePattern, setExcludePattern] = useState('')

  const addIncludeGlob = () => {
    if (includePattern.trim() && !includeGlobs.includes(includePattern.trim())) {
      setIncludeGlobs([...includeGlobs, ...includePattern.split(',').map((glob) => glob.trim())])
      setIncludePattern('')
    }
  }

  const addExcludeGlob = () => {
    if (excludePattern.trim() && !excludeGlobs.includes(excludePattern.trim())) {
      setExcludeGlobs([...excludeGlobs, ...excludePattern.split(',').map((glob) => glob.trim())])
      setExcludePattern('')
    }
  }

  const removeIncludeGlob = (glob: string) => {
    setIncludeGlobs(includeGlobs.filter((g) => g !== glob))
  }

  const removeExcludeGlob = (glob: string) => {
    setExcludeGlobs(excludeGlobs.filter((g) => g !== glob))
  }

  return (
    <div className="my-6 flex w-full flex-col justify-between gap-4">
      <div className="flex flex-col gap-4 px-0.5">
        {/* Include Patterns */}
        <div className="space-y-2">
          <Label htmlFor="include" className="text-sm">
            Include Patterns
            <span className="ml-2 text-xs text-muted-foreground">(URLs to crawl)</span>
          </Label>
          <div className="flex space-x-2">
            <Input
              id="include"
              value={includePattern}
              onChange={(e) => setIncludePattern(e.target.value)}
              placeholder="e.g., docs, /blog/*"
              onKeyUp={(e) => e.key === 'Enter' && addIncludeGlob()}
              onBlur={() => {
                if (includePattern.trim()) {
                  addIncludeGlob()
                }
              }}
              className="text-sm"
            />
            <Button onClick={addIncludeGlob} disabled={!includePattern.trim()} size="icon" variant={'secondary'}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {includeGlobs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {includeGlobs.map((glob, index) => (
                <Badge key={index} variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-200">
                  {glob}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-1 h-auto p-0 text-green-600 hover:bg-transparent hover:text-green-800"
                    onClick={() => removeIncludeGlob(glob)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Exclude Patterns */}
        <div className="space-y-2">
          <Label htmlFor="exclude">
            Exclude Patterns
            <span className="ml-2 text-xs text-muted-foreground">(URLs to skip)</span>
          </Label>
          <div className="flex space-x-2">
            <Input
              id="exclude"
              value={excludePattern}
              onChange={(e) => setExcludePattern(e.target.value)}
              placeholder="e.g., admin, /api/*"
              onKeyUp={(e) => e.key === 'Enter' && addExcludeGlob()}
              onBlur={() => {
                if (excludePattern.trim()) {
                  addExcludeGlob()
                }
              }}
              className="text-sm"
            />
            <Button onClick={addExcludeGlob} disabled={!excludePattern.trim()} size="icon" variant={'secondary'}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {excludeGlobs.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {excludeGlobs.map((glob, index) => (
                <Badge key={index} variant="destructive" className="bg-red-100 text-red-800 hover:bg-red-200">
                  {glob}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-1 h-auto p-0 text-red-600 hover:bg-transparent hover:text-red-800"
                    onClick={() => removeExcludeGlob(glob)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const DiscoverPagesFooter = ({
  footerRef,
  close,
  handleDiscoverUrls,
  isValidUrl,
  currentUrl,
  isLoading,
}: {
  footerRef: React.RefObject<HTMLDivElement>
  close: () => void
  handleDiscoverUrls: () => void
  isValidUrl: boolean
  currentUrl: string
  isLoading: boolean
}) => {
  const isMobile = useIsMobile()
  if (!footerRef.current) return null
  return createPortal(
    <AddKnowledgeSourceFooter
      linkName="Learn more about syncing websites"
      linkHref="https://botpress.com/docs/learn/reference/knowledge-base/add-sources#websites"
      showCancel={true}
      onClose={close}
      primaryAction={
        <AsyncButton
          size={isMobile ? 'default' : 'sm'}
          className="min-w-[100px]"
          loading={isLoading}
          disabled={!isValidUrl || !currentUrl || isLoading}
          onClick={handleDiscoverUrls}
        >
          Discover Pages
        </AsyncButton>
      }
    />,
    footerRef.current
  )
}
